/**
 * Secure session management utilities with automatic token refresh
 */

import { tokenManager } from './tokenManager';
import { clearAllStorage } from './storageCleanup';

export interface SessionConfig {
  maxIdleTime: number; // in milliseconds
  maxSessionTime: number; // in milliseconds
  warningTime: number; // in milliseconds before session expires
}

const DEFAULT_CONFIG: SessionConfig = {
  maxIdleTime: 15 * 60 * 1000, // 1 hour (changed from 30 minutes)
  maxSessionTime: 8 * 60 * 60 * 1000, // 8 hours
  warningTime: 5 * 60 * 1000, // 5 minutes
};

// Test configuration with shorter timeouts for debugging
const TEST_CONFIG: SessionConfig = {
  maxIdleTime: 2 * 60 * 1000, // 2 minutes for testing
  maxSessionTime: 10 * 60 * 1000, // 10 minutes for testing
  warningTime: 30 * 1000, // 30 seconds for testing
};

class SessionManager {
  private config: SessionConfig;
  private lastActivity: number = Date.now();
  private sessionStart: number = Date.now();
  private warningShown: boolean = false;
  private warningCallback?: () => void;
  private logoutCallback?: () => void;
  private activityCheckInterval?: NodeJS.Timeout;
  private warningTimeout?: NodeJS.Timeout;
  private isInitialized: boolean = false;

  constructor(config: Partial<SessionConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    // Don't start monitoring until explicitly initialized
  }

  private startActivityMonitoring() {
    if (this.isInitialized) {
      console.log('Session monitoring already started');
      return;
    }

    console.log('Starting session activity monitoring');
    this.isInitialized = true;
    
    // Check for activity every minute
    this.activityCheckInterval = setInterval(() => {
      this.checkSessionStatus();
    }, 60 * 1000);

    // Listen for user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, this.updateActivity.bind(this), true);
    });
    
    console.log('Session monitoring started with interval:', this.activityCheckInterval);
  }

  private updateActivity() {
    const previousActivity = this.lastActivity;
    this.lastActivity = Date.now();
    this.warningShown = false;
    
    // Debug logging for activity detection (only log if significant time has passed)
    const timeSinceLastActivity = this.lastActivity - previousActivity;
    if (timeSinceLastActivity > 10000) { // Only log if more than 10 seconds since last activity
      console.log('User activity detected after inactivity:', {
        timeSinceLastActivity: Math.round(timeSinceLastActivity / 1000) + 's',
        newLastActivity: new Date(this.lastActivity).toLocaleTimeString()
      });
    }
    
    // Clear existing warning timeout
    if (this.warningTimeout) {
      clearTimeout(this.warningTimeout);
    }

    // Check if we need to refresh tokens on user activity
    this.checkAndRefreshTokens();
  }

  private async checkAndRefreshTokens() {
    try {
      // Only refresh if tokens are close to expiry
      if (tokenManager.shouldRefreshTokens()) {
        await tokenManager.refreshTokens();
      }
    } catch (error) {
      console.error('Failed to refresh tokens:', error);
      // If token refresh fails, we'll let the normal session timeout handle it
    }
  }

  private checkSessionStatus() {
    // Only check if we're properly initialized
    if (!this.isInitialized) {
      console.log('Session manager not initialized, skipping status check');
      return;
    }

    const now = Date.now();
    const idleTime = now - this.lastActivity;
    const sessionTime = now - this.sessionStart;

    // Debug logging
    console.log('Session Status Check:', {
      idleTimeMinutes: Math.round(idleTime / (1000 * 60)),
      sessionTimeMinutes: Math.round(sessionTime / (1000 * 60)),
      maxIdleTimeMinutes: Math.round(this.config.maxIdleTime / (1000 * 60)),
      maxSessionTimeMinutes: Math.round(this.config.maxSessionTime / (1000 * 60)),
      warningShown: this.warningShown,
      hasLogoutCallback: !!this.logoutCallback
    });

    // Check if session has exceeded maximum time
    if (sessionTime > this.config.maxSessionTime) {
      console.log('Session expired due to maximum session time');
      this.forceLogout('Session expired due to maximum session time');
      return;
    }

    // Check if user has been idle too long
    if (idleTime > this.config.maxIdleTime) {
      console.log('Session expired due to inactivity');
      this.forceLogout('Session expired due to inactivity');
      return;
    }

    // Show warning if approaching session expiry
    const timeUntilExpiry = Math.min(
      this.config.maxSessionTime - sessionTime,
      this.config.maxIdleTime - idleTime
    );

    if (timeUntilExpiry <= this.config.warningTime && !this.warningShown) {
      console.log('Showing session warning');
      this.showWarning();
    }
  }

  private showWarning() {
    this.warningShown = true;
    
    if (this.warningCallback) {
      this.warningCallback();
    }

    // Set timeout to force logout after warning period
    this.warningTimeout = setTimeout(() => {
      this.forceLogout('Session expired after warning period');
    }, this.config.warningTime);
  }

  private forceLogout(reason: string) {
    console.log('Force logout triggered:', reason);
    
    // Clear all timeouts and intervals
    if (this.activityCheckInterval) {
      clearInterval(this.activityCheckInterval);
    }
    if (this.warningTimeout) {
      clearTimeout(this.warningTimeout);
    }

    // Remove event listeners
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.removeEventListener(event, this.updateActivity.bind(this), true);
    });

    // Clear session data
    this.clearSessionData();

    // Call logout callback
    if (this.logoutCallback) {
      console.log('Calling logout callback');
      this.logoutCallback();
    } else {
      console.log('No logout callback set');
    }
  }

  private clearSessionData() {
    // Use comprehensive cleanup to clear all storage, cookies, and cache
    clearAllStorage();
    
    // Clear session storage (clearAllStorage already clears sessionStorage, but keep these specific ones for clarity)
    sessionStorage.removeItem('processed_auth_code');
    sessionStorage.removeItem('processing_auth_code');
  }

  public setWarningCallback(callback: () => void) {
    this.warningCallback = callback;
  }

  public setLogoutCallback(callback: () => void) {
    this.logoutCallback = callback;
  }

  public resetSession() {
    console.log('Resetting session');
    this.lastActivity = Date.now();
    this.sessionStart = Date.now();
    this.warningShown = false;
    
    // Clear existing warning timeout
    if (this.warningTimeout) {
      clearTimeout(this.warningTimeout);
    }
    
    // Start monitoring if not already started
    if (!this.isInitialized) {
      this.startActivityMonitoring();
    }
  }

  public getSessionInfo() {
    const now = Date.now();
    const idleTime = now - this.lastActivity;
    const sessionTime = now - this.sessionStart;
    
    return {
      idleTime,
      sessionTime,
      timeUntilIdleExpiry: this.config.maxIdleTime - idleTime,
      timeUntilSessionExpiry: this.config.maxSessionTime - sessionTime,
      isWarningShown: this.warningShown
    };
  }

  public destroy() {
    console.log('Destroying session manager');
    
    if (this.activityCheckInterval) {
      clearInterval(this.activityCheckInterval);
      this.activityCheckInterval = undefined;
    }
    if (this.warningTimeout) {
      clearTimeout(this.warningTimeout);
      this.warningTimeout = undefined;
    }

    // Remove event listeners
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.removeEventListener(event, this.updateActivity.bind(this), true);
    });
    
    // Reset state
    this.isInitialized = false;
    this.warningCallback = undefined;
    this.logoutCallback = undefined;
  }

  // Method to manually trigger session check (for testing)
  public manualSessionCheck() {
    console.log('Manual session check triggered');
    this.checkSessionStatus();
  }

  // Method to simulate user activity (for testing)
  public simulateActivity() {
    console.log('Simulating user activity');
    this.updateActivity();
  }

  // Method to switch to test mode with shorter timeouts
  public enableTestMode() {
    console.log('Enabling test mode with shorter timeouts');
    this.config = { ...TEST_CONFIG };
    this.resetSession();
  }

  // Method to switch back to production mode
  public enableProductionMode() {
    console.log('Enabling production mode with normal timeouts');
    this.config = { ...DEFAULT_CONFIG };
    this.resetSession();
  }

  // Method to check if session manager is properly initialized
  public getStatus() {
    return {
      isInitialized: this.isInitialized,
      hasWarningCallback: !!this.warningCallback,
      hasLogoutCallback: !!this.logoutCallback,
      lastActivity: new Date(this.lastActivity).toLocaleTimeString(),
      sessionStart: new Date(this.sessionStart).toLocaleTimeString(),
      config: this.config
    };
  }

  // Method to force start monitoring (for debugging)
  public forceStartMonitoring() {
    console.log('Force starting session monitoring');
    this.startActivityMonitoring();
  }
}

// Export singleton instance
export const sessionManager = new SessionManager();

export default SessionManager;
